import React, { useState, useEffect } from 'react';

import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Rating from '@mui/material/Rating';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import { colours } from 'src/constants/colours';

import type { Medium, RatingsQuery } from 'src/types/__generated__/graphql';

import Icon from 'src/components/ui/Icon';
import Typography from 'src/components/ui/Typography';
import ExpandableText from 'src/components/ui/ExpandableText';
import HideUnhideRatingModal from 'src/components/profile/HideUnhideRatingModal';
import Dialog from 'src/components/ui/Dialog';
import theme from 'src/utils/theme';
import formatDistanceToNow from 'src/utils/formatDistanceToNow';
import Avatar from 'src/components/ui/Avatar';
import useAppRouter from 'src/hooks/useAppRouter';
import { routePathProfileRatings } from 'src/constants/router';
import { styledComponent, styledTag } from 'src/utils/styled';

const StyledRating = styledComponent(Rating)`
    font-size: 1.25rem;
    color: ${theme.palette.tertiary.main};
    .MuiSvgIcon-root {
        color: ${theme.palette.tertiary.main};
    }
    .MuiRating-iconEmpty {
        color: transparent;
        .MuiSvgIcon-root {
            color: ${theme.palette.tertiary.main};
        }
    }
`;

const StyledRatingHeader = styledComponent(CardHeader)`
    .MuiCardHeader-avatar {
        margin-right: 0.75rem;
    }
    .MuiCardHeader-title {
        font-size: 1rem;
    }
    .MuiCardHeader-subheader {
        text-transform: capitalize;
    }
    .MuiCardHeader-action {
        margin: 0;
        align-self: center;
    }
`;
const StyledRatingContent = styledComponent(CardContent)`
    padding-top: 0;
    margin-bottom: auto;
`;
const StyledRatingCard = styledComponent(Card)`
    margin: -1rem;
    height: calc(100% + 2rem);
`;
StyledRatingCard.defaultProps = {
    elevation: 0,
};

const StyledRatingActionArea = styledComponent(CardActionArea)`
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
`;

const StyledDialog = styledComponent(Dialog)`
    .MuiDialog-paper {
        min-width: 24rem;
    }
`;

const StyledBox = styledComponent(Box)`
    border-radius: 100%;
    background: ${colours.orangePill};
    position: relative;
    height: 2.5rem;
    width: 2.5rem;
`;

const StyledAvatar = styledComponent(Avatar)<{
    $hide?: boolean;
}>`
    opacity: ${({ $hide }) => ($hide ? '0.3' : '')};
`;

const IconContainer = styledTag('div')`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
`;

type RatingType = NonNullable<ArrayElement<NonNullable<RatingsQuery['ratings']>['allRatings']>>;

interface CardProps {
    rating: RatingType;
}

const RatingCard: React.FC<CardProps> = ({ rating }) => {
    const { t, i18n } = useTranslation(['profile', 'common']);
    const [open, setOpen] = useState(false);
    const router = useAppRouter();
    const userCompanyId = (router.query.userId ?? router.query.companyId) as string;

    const isEditProfile = router.pathname === routePathProfileRatings;

    const handleClickOpen = () => {
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
    };

    const cardHeader = (
        <StyledRatingHeader
            avatar={
                <StyledBox>
                    <IconContainer>
                        <Icon size="1.5rem" color={colours.white.main} name="Union" />
                    </IconContainer>
                    <StyledAvatar
                        $hide={Boolean(isEditProfile && rating.isHidden)}
                        alt={rating.giver?.fullName}
                        image={rating.giver?.image as Medium}
                        fallbackText={rating.giver?.initials}
                        size={40}
                    />
                </StyledBox>
            }
            title={
                <Typography color="primary">
                    <strong>{rating.giver?.firstName ?? rating.giver?.fullName}</strong> ·{' '}
                    <small>{formatDistanceToNow(String(rating.createdAt), i18n.language as SupportedLangs)}</small>
                </Typography>
            }
            action={
                <Box display="flex" alignItems="center">
                    <StyledRating value={Number(rating.value)} readOnly />
                    <Box ml={1}>
                        <Typography variant="body2">
                            <small>{rating.value}</small>
                        </Typography>
                    </Box>
                </Box>
            }
        />
    );

    useEffect(handleClose, [userCompanyId]);

    return (
        <Box key={rating.id}>
            <StyledRatingCard>
                <StyledRatingActionArea
                    onClick={() => (isEditProfile ? handleClickOpen() : undefined)}
                    disableRipple={!isEditProfile}
                    style={{ cursor: isEditProfile ? 'pointer' : 'default' }}
                >
                    {cardHeader}
                    <StyledRatingContent>
                        <Typography variant="body2">
                            <ExpandableText text={rating.comment} />
                        </Typography>
                    </StyledRatingContent>
                </StyledRatingActionArea>
            </StyledRatingCard>

            {isEditProfile && (
                <StyledDialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open}>
                    <HideUnhideRatingModal onClose={handleClose} rating={rating} />
                </StyledDialog>
            )}
        </Box>
    );
};

export default RatingCard;
